/**
 * IncidentPhotosSection — evidence photo gallery + camera/file upload
 * for a single incident. Reused inside IncidentDetailPage.
 *
 * Upload uses multipart to /api/incidents/{id}/photos which stores
 * via the same object-storage pipeline as project photos. Thumbnails
 * are served via the existing /api/files/{file_id}/download?size=thumb
 * endpoint, so cache + resize behavior is inherited for free.
 */
import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Camera, Upload, X, ImageOff } from "lucide-react";

export default function IncidentPhotosSection({ incidentId, canEdit, onChanged }) {
  const [photos, setPhotos] = useState(null); // null = loading; [] = loaded empty
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const reload = () => {
    api.get(`/incidents/${incidentId}/photos`)
      .then((r) => setPhotos(r.data.items || []))
      .catch(() => setPhotos([]));
  };
  useEffect(reload, [incidentId]);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/incidents/${incidentId}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setUploading(false);
    if (ok) toast.success(`Uploaded ${ok} photo${ok === 1 ? "" : "s"}.`);
    if (fail) toast.error(`${fail} upload${fail === 1 ? "" : "s"} failed.`);
    reload();
    onChanged?.();
  };

  const onFileInput = (e) => {
    uploadFiles(Array.from(e.target.files || []));
    e.target.value = ""; // allow re-select of same file
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (!canEdit) return;
    uploadFiles(Array.from(e.dataTransfer?.files || []));
  };

  const removePhoto = async (fileId) => {
    if (!window.confirm("Remove this photo from the incident?")) return;
    try {
      await api.delete(`/incidents/${incidentId}/photos/${fileId}`);
      reload();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const hasPhotos = photos && photos.length > 0;

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="incident-photos-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-display font-bold">Evidence Photos</div>
            <div className="font-display font-bold text-sm">
              {photos === null
                ? "Loading…"
                : hasPhotos
                  ? `${photos.length} photo${photos.length === 1 ? "" : "s"} attached`
                  : "No photos yet"}
            </div>
          </div>
          {canEdit && (
            <div className="flex gap-1.5">
              <Button
                size="sm"
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploading}
                className="bg-red-700 hover:bg-red-600 text-xs h-8"
                data-testid="incident-photos-camera-btn"
              >
                <Camera className="w-3.5 h-3.5 mr-1" /> Take Photo
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="border-slate-700 bg-slate-950 hover:bg-slate-800 text-xs h-8"
                data-testid="incident-photos-upload-btn"
              >
                <Upload className="w-3.5 h-3.5 mr-1" /> Upload
              </Button>
              {/* Hidden inputs — separate so we can force capture=environment
                  on mobile for the "Take Photo" flow (opens the rear camera
                  directly) while keeping the general upload picker unlocked. */}
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                onChange={onFileInput} className="hidden"
                data-testid="incident-photos-camera-input" />
              <input ref={fileInputRef} type="file" accept="image/*" multiple
                onChange={onFileInput} className="hidden"
                data-testid="incident-photos-file-input" />
            </div>
          )}
        </div>

        {/* Drop zone (also shows the empty state when nothing's attached). */}
        <div
          onDragOver={(e) => { if (canEdit) { e.preventDefault(); setDragging(true); } }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`rounded-md border-2 border-dashed p-4 transition-colors ${
            dragging ? "border-blue-500 bg-blue-950/30" : "border-slate-800 bg-slate-950"
          }`}
          data-testid="incident-photos-dropzone"
        >
          {uploading && (
            <div className="text-center text-xs text-blue-300 mb-2 animate-pulse">Uploading…</div>
          )}
          {!hasPhotos ? (
            <div className="text-center py-4 text-xs text-slate-500 flex flex-col items-center gap-1">
              <ImageOff className="w-6 h-6 text-slate-600" />
              {canEdit
                ? "Drop photos here, or use the buttons above."
                : "No evidence photos have been attached yet."}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="incident-photos-grid">
              {photos.map((p) => (
                <div
                  key={p.id}
                  className="relative aspect-square rounded overflow-hidden border border-slate-800 bg-slate-900 group"
                  data-testid={`incident-photo-${p.id}`}
                >
                  <a
                    href={fileUrl(p.id, { size: "full" })}
                    target="_blank" rel="noopener noreferrer"
                    className="block w-full h-full"
                    title={p.original_filename}
                  >
                    <img
                      src={fileUrl(p.id, { size: "thumb" })}
                      alt={p.original_filename}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </a>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => removePhoto(p.id)}
                      className="absolute top-1 right-1 p-1 rounded-full bg-red-950/90 hover:bg-red-800 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Remove photo"
                      data-testid={`incident-photo-remove-${p.id}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
